import { InjectionKey, Ref, createApp, reactive, ref, watch, h } from 'vue';
import { simpleCopy } from '@/utils/deepCopy';
import { defaultImportFlags, defaultInputFileInfo, defaultXlsxFormFileInfo } from '@/appLogic/importData/initialData';
import { i18n } from '@/i18n';
import { FileUploadOptions, useDropzone } from 'vue3-dropzone';
import { unzipForFile } from '@/utils/translateExcel/zip';
import { bulkReadAnnounceExcel, bulkReadDailyAnnounceExcel } from '@/utils/translateExcel/readExcel';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import {
  createModalActionResult,
  ModalActionResult,
  openConfirmModal,
  openFailToSavedModal,
  openOkInformationModal,
} from '@/corePagesModule/components/modal/modalActions';
import {
  ExcelTranslateError,
  IOutputFileInfo,
  OutputFileContents,
  OutputFileType,
  TranslateExcelBusiness,
  translateExcelBusinessProperties,
  TranslateKeyName,
} from '@/coreDomainsModule/translateExcel';
import { DatePickerService } from '@/corePagesModule/service/datepickerService';
import { InventoryService } from '@/coreDomainsModule/services/inventoryService';
import { CreateCsvUseCase } from '@/exportFileUseCasesModule/createCsvUseCase';
import { AlterableHeaderService } from '@/coreDomainsModule/entities/inputExcelInfo';
import { TranslateInputFileManager } from '@/appLogic/fileManager/translate/translateInputFileManager';
import { TranslateOutputFileManager } from '@/appLogic/fileManager/translate/translateOutputFileManage';
import { registerBusinessForTranslateExcelBusiness } from '@/appLogic/fileManager/translate/registerBusiness';
import { extendedBusinessToIBusiness, IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DAY_STD } from '@/coreDomainsModule/constants';
import { ImportFileType, IUnmanagedBusiness } from '@/coreDomainsModule/entities/unmanagedBusiness';
import { UnmanagedBusinessFactory } from '@/coreDomainsModule/factories/UnmanagedBusinessFactory';
import { UnmanagedBusinessService } from '@/coreDomainsModule/services/unmanagedBusinessService';
import { assertNever } from '@/coreDomainsModule/assertNever';
import { getExcelTypeFromFileName } from '@/appLogic/fileManager/translate/excelUtils';
import { InputExcelInfosRepository } from '@/coreInfrastructureModule/Repositories/InputExcelInfosRepository';
import { FetchExcelModal, ResultRegisteredAnnounce, TranslateExcelArgs } from '@/registerInputModule/pages/interfaces/translateExcelStore';
import { GetInputExcelInfoUseCase } from '@/registerInputModule/useCases/GetInputExcelInfoUseCase';
import { dateField } from '@/corePagesModule/uiFactory/fetchExcelModal';
import Calendar from '@/corePagesModule/components/Calendar.vue';
import { diContainer } from '@/coreContainer/container';
import {
  ANNOUNCE_AMOUNT_COMPARISON_TOLERANCE,
  DAILY_ANNOUNCE_PERIOD_THRESHOLD_DAYS,
  DISPLAY_FORECAST_PERIOD,
} from '@/registerInputModule/constants/importExcelConstants';
import { DAILY_ANNOUNCE_START_DAY_CELL } from '@/appLogic/fileManager/translate/constants/importExcelConstants';
import { handleFileInput } from '@/orderTableModule/importOrderExcelManager';
import {
  hasAlertCount,
  RegisterOrderBusinessResultCounts,
  showOrderImportSuccessModalContent,
  TranslateExcelOrderBusiness,
} from '@/coreDomainsModule/translateExcelOrder';

// TODO: expectedFileExtensionの型を修正する(行方不明になったのでいったんanyにしている)

const translateInputFileManager = new TranslateInputFileManager();

// STOPSHIP: ざっくり移行しているだけなので、リファクタ必須

/**
 * store
 * @param args - 画面遷移時に受け取るパラメータ
 */
export const useImportExcelStore = (args?: TranslateExcelArgs): ITranslateExcelStore => {
  const state = reactive<FetchExcelModal>({
    showTable: false,
    showModal: false,
    zoomPic: '',
    searchWord: '',
    dict: 'photo',
    xlsxFormFileInfo: simpleCopy(defaultXlsxFormFileInfo),
    testXlsxFormMergedInputArray: {},
    testXlsxFormMergedOutputArray: {},
    orderBusinessArray: {},
    inputFiles: simpleCopy(defaultInputFileInfo),
    outputOrderFile: undefined,
    outputSupplierNotificationFile: undefined,
    files: [],
    fileReadErrors: [],
    importFlags: simpleCopy(defaultImportFlags),
    activeLoading: false,
    executionDate: new Date(),
    importAnnounceExcelResult: { manageInitiatedForecastDate: {}, isImport: false },
    importAnnounceDailyExcelResult: { manageInitiatedForecastDate: {}, isImport: false },
    importInventoryExcelResult: {
      latestInventoryYearMonth: {
        year: 0,
        month: 0,
      },
      isImport: false,
    },
    successTranslateKeys: {},
    translateMode: 'updateExcel', // 固定
    inputExcelTypes: [],
    isOpenResultRegisteredAnnounceModal: false,
    resultRegisteredAnnounces: [],
  }) as FetchExcelModal;

  const t = i18n.t;
  let fileInputRef1: Ref<any>;
  let fileInputRef2: Ref<any>;
  let fileInputRef3: Ref<any>;
  let fileInputRef4: Ref<any>;
  let fileInputRef5: Ref<any>;
  let fileInputRef6: Ref<any>;
  let fileInputRef7: Ref<any>;
  let fileInputRef8: Ref<any>;

  const progressValue = ref(0);
  const progressTaskMessage = ref('');

  /**
   * ファイルの読み込み処理
   * @param ref1 - ファイル1
   * @param ref2 - ファイル2
   * @param ref3 - ファイル3
   * @param ref4 - ファイル4
   * @param ref5 - ファイル5
   * @param ref6 - ファイル6
   * @param ref7 - ファイル7
   * @param ref8 - ファイル8
   */
  const setupFileInputRef = (
    ref1: Ref<any>,
    ref2: Ref<any>,
    ref3: Ref<any>,
    ref4: Ref<any>,
    ref5: Ref<any>,
    ref6: Ref<any>,
    ref7: Ref<any>,
    ref8: Ref<any>
  ) => {
    fileInputRef1 = ref1;
    fileInputRef2 = ref2;
    fileInputRef3 = ref3;
    fileInputRef4 = ref4;
    fileInputRef5 = ref5;
    fileInputRef6 = ref6;
    fileInputRef7 = ref7;
    fileInputRef8 = ref8;
  };
  // 出力ファイル選択のボタン
  const outputFileSelectionButton = ref<IOutputFileInfo>({
    在庫表: {
      id: 1,
      label: '在庫表',
      active: false,
      isDisabled: true,
    },
    変更履歴: {
      id: 2,
      label: '変更履歴',
      active: false,
      isDisabled: true,
    },
    未転記取引まとめ: {
      id: 3,
      label: '未転記取引まとめ',
      active: false,
      isDisabled: true,
    },
    仕入先内示: {
      id: 4,
      label: '仕入先内示',
      active: false,
      isDisabled: true,
    },
    取引重複エラー: {
      // 使ってない(?)
      id: 5,
      label: '取引重複エラー',
      active: false,
      isDisabled: true,
    },
  });

  // vue-dropzoneを使い、ファイルリセット関数を作成する
  /**
   * ファイルリセット
   * @param file - ファイル
   */
  const onFileRemoved = (file) => {
    const fileName = file?.name ?? '';
    const fileExtension = fileName.split('.').pop();
    let fileType: TranslateKeyName | OutputFileType | 'none' = 'none';
    if (fileExtension === 'zip') {
      fileType = getAnnounceExcelTypeFromZipFileName(fileName);
    } else {
      fileType = getExcelTypeFromFileName(fileName);
    }
    if (fileType === 'none') return;
    switch (fileType) {
      case '在庫':
        state.xlsxFormFileInfo[fileType] = undefined;
        fileInputRef2?.value?.resetInputFile();
        changeImportFlags('在庫', false);
        removeFile(file, fileType);
        break;
      case '販売実績':
        state.xlsxFormFileInfo[fileType] = undefined;
        fileInputRef3?.value?.resetInputFile();
        changeImportFlags('販売実績', false);
        removeFile(file, fileType);
        break;
      case '入荷':
        state.xlsxFormFileInfo[fileType] = undefined;
        fileInputRef4?.value?.resetInputFile();
        changeImportFlags('入荷', false);
        removeFile(file, fileType);
        break;
      case '在庫表':
        state.xlsxFormFileInfo[fileType] = undefined;
        fileInputRef5?.value?.resetInputFile();
        state.outputOrderFile = undefined;
        changeImportFlags('在庫表', false);
        removeFile(file, fileType);
        break;
      case '内示':
        state.xlsxFormFileInfo[fileType] = undefined;
        fileInputRef1?.value?.resetInputFile();
        changeImportFlags('内示', false);
        removeFile(file, fileType);
        break;
      case '日別内示':
        state.xlsxFormFileInfo[fileType] = undefined;
        fileInputRef7?.value?.resetInputFile();
        changeImportFlags('日別内示', false);
        removeFile(file, fileType);
        break;
      case '仕入先内示':
        state.xlsxFormFileInfo[fileType] = undefined;
        fileInputRef6?.value?.resetInputFile();
        state.outputSupplierNotificationFile = undefined;
        changeImportFlags('仕入先内示', false);
        removeFile(file, fileType);
        break;
      case '発注取込':
        state.xlsxFormFileInfo[fileType] = undefined;
        fileInputRef8?.value?.resetInputFile();
        changeImportFlags('発注取込', false);
        removeFile(file, fileType);
      default:
        break;
    }
  };

  /**
   * ファイルを削除する
   * @param acceptFiles  - ファイル
   * @param rejectReasons - 理由
   */
  function onDrop(acceptFiles, rejectReasons) {
    const sameFileNames: string[] = [];
    const duplicateFileNames: string[] = [];
    acceptFiles.forEach((file) => {
      let isFileExist = false;
      state.files.forEach((f) => {
        if (file.name === (f as unknown as File).name) {
          // 同じファイル名かどうかの判定
          isFileExist = true;
          sameFileNames.push(file.name);
        } else if (file.name.split('_')[0] === (f as unknown as File).name.split('_')[0]) {
          // 同じ種類のエクセルファイルかどうかの判定
          isFileExist = true;
          duplicateFileNames.push(file.name);
        }
      });
      if (!isFileExist) {
        state.files.push(file);
        readFileFromDropzone(file);
      }
    });
    // 全く同じ名前のファイルが入力された時
    if (sameFileNames.length > 0) {
      showFileNameDuplicateErrorAlert(sameFileNames);
    }
    // ファイル属性が同じファイルが入力された時
    if (duplicateFileNames.length > 0) {
      showFileAttributeDuplicateErrorAlert(duplicateFileNames);
    }
  }
  const dropzoneOptions2: Partial<FileUploadOptions> = {
    onDrop: onDrop,
  };
  const { getRootProps, getInputProps, isDragActive, ...rest } = useDropzone(dropzoneOptions2);

  /**
   * 入力ファイル情報、ファイル読み込み情報、転記処理後のファイル情報などを全て初期化する
   */
  const removeAllFile = () => {
    removeInputFile();
    removeReadFileInfo();
  };

  /**
   * 入力ファイル情報を全て初期化する
   */
  const removeInputFile = () => {
    fileInputRef1?.value?.resetInputFile();
    fileInputRef2?.value?.resetInputFile();
    fileInputRef3?.value?.resetInputFile();
    fileInputRef4?.value?.resetInputFile();
    fileInputRef5?.value?.resetInputFile();
    fileInputRef6?.value?.resetInputFile();
    fileInputRef7?.value?.resetInputFile();
    fileInputRef8?.value?.resetInputFile();
    state.inputFiles = simpleCopy(defaultInputFileInfo);
    state.files = [];
    state.outputOrderFile = undefined;
    state.outputSupplierNotificationFile = undefined;
    state.importFlags = simpleCopy(defaultImportFlags);
    Object.keys(outputFileSelectionButton.value).forEach((key) => (outputFileSelectionButton.value[key].active = false));
  };

  /**
   * 読み込みファイル情報、転記処理後のファイル情報を初期化する
   */
  const removeReadFileInfo = () => {
    state.xlsxFormFileInfo = simpleCopy(defaultXlsxFormFileInfo);
    state.testXlsxFormMergedInputArray = {};
    state.testXlsxFormMergedOutputArray = {};
    state.fileReadErrors = [];
    state.activeLoading = false;
    state.executionDate = new Date();
    state.importInventoryExcelResult = {
      latestInventoryYearMonth: {
        year: 0,
        month: 0,
      },
      isImport: false,
    };
    state.importAnnounceExcelResult = {
      manageInitiatedForecastDate: {},
      isImport: false,
    };
    state.importAnnounceDailyExcelResult = {
      manageInitiatedForecastDate: {},
      isImport: false,
    };
    state.successTranslateKeys = {};
    state.inputExcelTypes = [];
  };

  /**
   * ファイルを削除する
   * @param file - ファイル
   */
  const isZipFile = (file: any): boolean => {
    const fileName: string = file?.name ?? '';
    return fileName.includes('.zip');
  };

  // vue3-dropzoneがこの機能を持っていなかったので仕方なく作っている
  // fileサイズを適切な文字列に変換する
  /**
   * ファイルサイズを適切な文字列に変換する
   * @param file - ファイル
   */
  const getFileSizeStr = (file: any): string => {
    const fileSize: number = file?.size ?? -1;
    if (fileSize === -1) return '';
    const kb = 1024;
    const mb = Math.pow(kb, 2);
    const gb = Math.pow(kb, 3);
    const tb = Math.pow(kb, 4);
    let unit = 'byte';
    let target = 1;
    if (fileSize >= tb) {
      target = tb;
      unit = 'TB';
    } else if (fileSize >= gb) {
      target = gb;
      unit = 'GB';
    } else if (fileSize >= mb) {
      target = mb;
      unit = 'MB';
    } else if (fileSize >= kb) {
      target = kb;
      unit = 'KB';
    } else {
      target = 1;
      unit = 'BYTE';
    }
    const res = target !== null ? Math.floor((fileSize / target) * 100) / 100 : fileSize;
    // 単位を付けて出力
    return res.toFixed(1) + unit;
  };

  /**
   * zipファイル名から内示種別を取得する
   * @param fileName - ファイル名
   */
  const getAnnounceExcelTypeFromZipFileName = (fileName: string): TranslateKeyName | 'none' => {
    if (fileName.includes('DailyForecast_') || fileName.includes('日別内示_')) {
      return '日別内示';
    }
    if (fileName.includes('Forecast_') || fileName.includes('内示_')) {
      return '内示';
    }
    return 'none';
  };

  /**
   * ドロップされたファイルを読み込む
   * @param file - ファイル
   */
  const readFileFromDropzone = (file: any) => {
    const fileName = file?.name;
    if (!fileName) return;
    const fileType = fileName.split('.').pop()?.toLowerCase();
    if (fileType === 'xlsx') {
      // ファイル名からファイルの種類を取得する
      const excelType = getExcelTypeFromFileName(fileName);
      switch (excelType) {
        case '在庫':
          state.xlsxFormFileInfo['在庫'] = file;
          fileRead(file, excelType, true, 'xlsx');
          break;
        case '販売実績':
          state.xlsxFormFileInfo['販売実績'] = file;
          fileRead(file, excelType, true, 'xlsx');
          break;
        case '入荷':
          state.xlsxFormFileInfo['入荷'] = file;
          fileRead(file, excelType, true, 'xlsx');
          break;
        case '在庫表':
          break;
        case '仕入先内示':
          break;
        case '内示':
          // NOTE: xlsx形式の内示ファイルは読み込まない
          break;
        case '日別内示':
          // NOTE: xlsx形式の日別内示ファイルは読み込まない
          break;
        case '発注取込':
          console.log(file);
          state.xlsxFormFileInfo['発注取込'] = file;
          fileRead(file, excelType, true, 'xlsx');
          break;
        case 'none':
          break;
        default:
          assertNever(excelType);
          break;
      }
    } else if (fileType === 'zip') {
      // .zip
      // zipファイルを判断するための.dz-file-preview-zipのクラスを追加する
      // file.previewElement.classList.add('dz-file-preview-zip');
      const excelType = getAnnounceExcelTypeFromZipFileName(fileName);
      if (excelType === 'none') return;
      state.xlsxFormFileInfo[excelType] = file;
      fileRead(file, excelType, true, 'zip');
    }
  };

  /**
   * ファイルを削除する
   * @param index - インデックス
   */
  const handleClickDeleteFile = function (index) {
    onFileRemoved(state.files[index]);
    state.files.splice(index, 1);
  };

  /**
   * 入力ファイルの組み合わせで、出力ファイル選択ボタンの状態を変えて、登録スタートボタンの状態を返す
   * @param correctFileNumber - 集計ファイル番号
   * @returns 登録スタートボタンの状態
   */
  const outputFlagSwitching = (correctFileNumber: number): boolean => {
    const hasAnnounce = state.importFlags['内示'] || state.importFlags['日別内示'];
    // 在庫表が入っている時
    if (state.importFlags['在庫表'] && !state.importFlags['仕入先内示'] && correctFileNumber > 1) {
      changeActiveFlg('仕入先内示');
      specifiedOutputFileSelectButtonDisabledAttach('仕入先内示');
      return true;
    }
    // 仕入先内示が入っている時
    if (!state.importFlags['在庫表'] && state.importFlags['仕入先内示'] && correctFileNumber > 1) {
      // 内示と仕入先内示がセットの時以外はボタンをアクティブにしない
      if (hasAnnounce) {
        changeActiveFlg('在庫表');
        specifiedOutputFileSelectButtonDisabledAttach('在庫表');
        return true;
      } else {
        Object.keys(outputFileSelectionButton.value).forEach((key) => (outputFileSelectionButton.value[key].active = false));
        Object.keys(outputFileSelectionButton.value).forEach((key) => (outputFileSelectionButton.value[key].isDisabled = true));
        outputFileSelectionButton.value['取引重複エラー'].active = false;
        return false;
      }
    }
    // 在庫表と仕入先内示どちらも入っている場合
    if (state.importFlags['在庫表'] && state.importFlags['仕入先内示'] && correctFileNumber > 1) {
      if (hasAnnounce) {
        Object.keys(outputFileSelectionButton.value).forEach((key) => (outputFileSelectionButton.value[key].active = true));
        Object.keys(outputFileSelectionButton.value).forEach((key) => (outputFileSelectionButton.value[key].isDisabled = false));
        outputFileSelectionButton.value['取引重複エラー'].active = false;
        return true;
      } else {
        Object.keys(outputFileSelectionButton.value).forEach((key) => (outputFileSelectionButton.value[key].active = false));
        Object.keys(outputFileSelectionButton.value).forEach((key) => (outputFileSelectionButton.value[key].isDisabled = true));
        return false;
      }
    } else {
      // 在庫表と仕入先内示どちらも入っていない時は押せなくする
      Object.keys(outputFileSelectionButton.value).forEach((key) => (outputFileSelectionButton.value[key].active = false));
      Object.keys(outputFileSelectionButton.value).forEach((key) => (outputFileSelectionButton.value[key].isDisabled = true));
      outputFileSelectionButton.value['取引重複エラー'].active = false;
      return false;
    }
  };

  // 入力されたファイルに応じて出力ファイル選択ボタンの状態を切り替える
  /**
   * 出力ファイル選択ボタンの状態を変更する
   * @param fileType - ファイル種別
   */
  const changeActiveFlg = (fileType: string): void => {
    Object.keys(outputFileSelectionButton.value).forEach((key) => {
      if (key === fileType) return (outputFileSelectionButton.value[key].active = false); // 対象のボタンを非アクティブに
      if (key === '取引重複エラー') return (outputFileSelectionButton.value[key].active = false); // 取引重複エラーは常に非アクティブに
      outputFileSelectionButton.value[key].active = true;
    });
  };

  /**
   * 指定した出力ファイル選択ボタンにdisabledを付け、それ以外からdisabledを外す
   * @param fileType - ファイル種別
   */
  const specifiedOutputFileSelectButtonDisabledAttach = (fileType: string): void => {
    Object.keys(outputFileSelectionButton.value).forEach((key) => {
      if (key === fileType) return (outputFileSelectionButton.value[key].isDisabled = true); // 対象のボタンをdisabledに
      if (key === '取引重複エラー') return (outputFileSelectionButton.value[key].isDisabled = true); // 取引重複エラーは常にdisabledに
      outputFileSelectionButton.value[key].isDisabled = false;
    });
  };

  // ファイル名とファイルタイプのバリデーションをチェックする関数
  /**
   * ファイル名とファイルタイプをチェックする
   * @param file - ファイル
   * @param fileType - ファイルタイプ
   * @param expectedFileExtension - 期待されるファイル拡張子
   */
  const validFiles = (file: any, fileType: TranslateKeyName | OutputFileType, expectedFileExtension: any) => {
    if (!file) return false;
    const fileName: string = file?.name;
    const fileExtension = fileName.split('.').pop()?.toLowerCase();

    // const listFileName = ['内示_', '在庫_', '販売実績_', '入荷_', '在庫表_', '仕入先内示_']; // 有効なファイル名の一覧
    const validFileName = fileType + '_';
    // ファイル名とファイルタイプをチェックする関数
    // TODO: booleanではなく、エラーをthrowするように書き換えたい（時間とって）
    // if (!fileName.includes(validFileName)) {
    //   return false;
    // } else if (fileExtension !== expectedFileExtension) {
    //   return false;
    // }
    // 英語ファイル名対応処理
    switch (fileType) {
      case '在庫表':
        if (!fileName.includes('MasterData_') && !fileName.includes(validFileName)) return false;
        break;
      case '仕入先内示':
        // FIXME: 定義がないので、一旦そのまま
        if (!fileName.includes(validFileName)) return false;
        break;
      case '販売実績':
        if (!fileName.includes('SalesResult_') && !fileName.includes(validFileName)) return false;
        break;
      case '入荷':
        if (!fileName.includes('Arrival_') && !fileName.includes(validFileName)) return false;
        break;
      case '在庫':
        if (!fileName.includes('Stock_') && !fileName.includes(validFileName)) return false;
        break;
      case '内示':
        if (!fileName.includes('Forecast_') && !fileName.includes(validFileName)) return false;
        break;
      case '日別内示':
        if (!fileName.includes('DailyForecast_') && !fileName.includes(validFileName)) return false;
        break;
      case '発注取込':
        // TODO 合ってる？
        if (!fileName.includes('Order_') && !fileName.includes(validFileName)) return false;
        break;
      default:
        assertNever(fileType);
    }

    if (fileExtension !== String(expectedFileExtension).toLowerCase()) return false;
    return true;
  };

  // Excelデータの読込
  /**
   * ファイルを読み込む
   * @param e - イベント
   * @param fileType - ファイルタイプ
   * @param isImportDragAndDrop  - ドラッグアンドドロップでのインポートかどうか
   * @param expectedFileExtension - 期待されるファイル拡張子
   */
  const fileRead = (e: any, fileType: TranslateKeyName | OutputFileType, isImportDragAndDrop = false, expectedFileExtension: any) => {
    const uploadedFile = isImportDragAndDrop ? e : (e.target as HTMLInputElement)?.files?.[0];
    // 選択されたファイルをチェックする関数を呼び出す
    if (!validFiles(uploadedFile, fileType, expectedFileExtension)) return;
    let file = (e.target as HTMLInputElement)?.files?.[0];
    if (isImportDragAndDrop) file = e;
    if (!file) return;
    if (fileType === '在庫表') {
      // 出力ファイルにもなる
      state.outputOrderFile = file;
    } else if (fileType === '仕入先内示') {
      // 出力ファイルにもなる
      state.outputSupplierNotificationFile = file;
    } else {
      state.inputExcelTypes.push(fileType);
    }
    state.inputFiles[fileType] = file;
    if (!isImportDragAndDrop) state.files.push(file as any); // ドロップゾーンのUIにも反映させるため
    changeImportFlags(fileType, true);
  };

  // zipファイルの解凍→xlsxFormArray1(内示エクセルによって取得されたIBusiness)を作成
  /**
   * zipファイルを解凍し、内示エクセルによって取得されたIBusinessを作成する
   * @param e - イベント
   * @param isImportDragAndDrop - ドラッグアンドドロップでのインポートかどうか
   */
  const createIBusinessFromZip = async (e: any, isImportDragAndDrop = false) => {
    // 存在チェック＆バリデーション
    const fileValid = isImportDragAndDrop ? e : (e.target as HTMLInputElement)?.files?.[0];
    // 選択されたファイルをチェックする関数を呼び出す
    if (!validFiles(fileValid, '内示', 'zip')) return;
    let file = (e.target as HTMLInputElement)?.files?.[0];
    if (isImportDragAndDrop) file = e;
    if (!file) return;

    // 解凍
    const unZippedFiles: File[] = await unzipForFile(file);
    // まとめて取り込む
    await bulkReadAnnounceExcel(
      unZippedFiles,
      state.testXlsxFormMergedInputArray,
      state.fileReadErrors,
      state.importAnnounceExcelResult.manageInitiatedForecastDate,
      translateInputFileManager,
      progressUpdate
    );
    // UIに反映
    if (!isImportDragAndDrop) state.files.push(file as any); // ドロップゾーンのUIにも反映させるため
    changeImportFlags('内示', true);
  };

  // zipファイルの解凍→xlsxFormArray1(日別内示エクセルによって取得されたIBusiness)を作成
  /**
   * zipファイルを解凍し、日別内示エクセルによって取得されたIBusinessを作成する
   * @param e - イベント
   * @param isImportDragAndDrop - ドラッグアンドドロップでのインポートかどうか
   */
  const createIBusinessFromDailyZip = async (e: any, isImportDragAndDrop = false) => {
    const fileValid = isImportDragAndDrop ? e : (e.target as HTMLInputElement)?.files?.[0];
    if (!validFiles(fileValid, '日別内示', 'zip')) return;
    let file = (e.target as HTMLInputElement)?.files?.[0];
    if (isImportDragAndDrop) file = e;
    if (!file) return;

    const unZippedFiles: File[] = await unzipForFile(file);
    await bulkReadDailyAnnounceExcel(
      unZippedFiles,
      state.testXlsxFormMergedInputArray,
      state.fileReadErrors,
      state.importAnnounceDailyExcelResult.manageInitiatedForecastDate,
      translateInputFileManager,
      progressUpdate
    );
    if (!isImportDragAndDrop) state.files.push(file as any);
    changeImportFlags('日別内示', true);
  };

  /**
   * 発注エクセルの中身を取り込んでstateに格納する
   */
  const createOrderBusinessFromXlsx = async () => {
    const targetFile = state.inputFiles['発注取込'];
    if (!targetFile) return;

    const { splitOrderExcelData, isSuccess } = await handleFileInput(targetFile);
    if (!isSuccess) {
      state.fileReadErrors.push({
        key: '',
        errCode: 'FailedInputExcel',
        errorFileName: {
          inputFileName: targetFile.name,
        },
        preCellNumber: '',
        errorSheetName: {
          inputSheetName: '',
          registerSheetName: '',
        },
        errorCellNumber: {
          inputCellNumber: '',
          registerCellNumber: '',
        },
        value: undefined,
        errorString: 'ファイル名、又はシート名が読み込めませんでした。フォーマットを再度確認し、修正してください。',
      });
    }

    state.orderBusinessArray = splitOrderExcelData;
  };

  /**
   * 指定したファイルタイプの入力済みフラグを変更する
   * @param fileType - ファイルタイプ
   * @param flag - フラグ
   */
  const changeImportFlags = (fileType: TranslateKeyName | OutputFileType, flag) => {
    state.importFlags[fileType] = flag;
  };

  /**
   * ファイルを入力する
   */
  const _readInputExcel = async () => {
    // エクセル出力
    state.activeLoading = true;

    // storageから入力情報を取得する
    const inputExcelInfosRepository = new InputExcelInfosRepository();
    const res = await new GetInputExcelInfoUseCase().Execute(inputExcelInfosRepository); // NOTE: 仕組み上絶対に取得できるのでハンドリングしてない
    const excelInputInfo = res.status.type === 'success' ? res.status.data : undefined;

    let deferredAnnounceFile: File | undefined;

    const invalidExcelInputInfoType: ('inventory' | 'salesResult' | 'arrival')[] = [];

    // 入力ファイルを読み込む
    for (const key of Object.keys(state.inputFiles)) {
      if (!state.inputFiles[key]) continue; // state.inputFilesの要素がundefinedのものは読みたくない
      switch (key) {
        case '内示':
          if (state.importFlags['日別内示']) {
            deferredAnnounceFile = state.inputFiles[key]!;
            break;
          }
          await createIBusinessFromZip(state.inputFiles[key], true); //zip解凍とファイルのコンバート処理を実行
          break;
        case '日別内示':
          await createIBusinessFromDailyZip(state.inputFiles[key], true);
          break;
        // 23/1/20 一旦共通のBusiness群として扱ってみる
        case '在庫':
          // 在庫エクセル
          if (!excelInputInfo || !excelInputInfo.Inventory) {
            invalidExcelInputInfoType.push('inventory');
            continue;
          }

          await translateInputFileManager.readInventoryExcel(
            state.inputFiles[key]!,
            state.testXlsxFormMergedInputArray,
            excelInputInfo.Inventory,
            AlterableHeaderService.getAlterableYearMonthHeaderValue(
              'Inventory',
              excelInputInfo.Inventory.selectYearMonthPattern,
              excelInputInfo.Inventory.alternateHeaderInfos
            ),
            state.fileReadErrors,
            state.inputFiles[key]!.name,
            ...[translateInputFileManager.STOCK_MAPPER]
          );
          await progressUpdate(state.inputFiles[key]!.name + t('読み込み中・・・'), 0);
          break;
        case '販売実績':
          // 販売実績エクセル
          if (!excelInputInfo || !excelInputInfo.SalesResults) {
            invalidExcelInputInfoType.push('salesResult');
            continue;
          }
          await translateInputFileManager.readSalesResultExcel(
            state.inputFiles[key]!,
            state.testXlsxFormMergedInputArray,
            excelInputInfo.SalesResults,
            AlterableHeaderService.getAlterableYearMonthHeaderValue(
              'SalesResults',
              excelInputInfo.SalesResults.selectYearMonthPattern,
              excelInputInfo.SalesResults.alternateHeaderInfos
            ),
            state.fileReadErrors,
            state.inputFiles[key]!.name,
            ...[translateInputFileManager.SALES_RESULTS_MAPPER]
          );
          await progressUpdate(state.inputFiles[key]!.name + t('読み込み中・・・'), 0);
          break;
        case '入荷':
          // 入荷履歴エクセル
          if (!excelInputInfo || !excelInputInfo.Arrival) {
            invalidExcelInputInfoType.push('arrival');
            continue;
          }
          await translateInputFileManager.readArrivalExcel(
            state.inputFiles[key]!,
            state.testXlsxFormMergedInputArray,
            excelInputInfo.Arrival,
            AlterableHeaderService.getAlterableYearMonthHeaderValue(
              'Arrival',
              excelInputInfo.Arrival.selectYearMonthPattern,
              excelInputInfo.Arrival.alternateHeaderInfos
            ),
            state.fileReadErrors,
            state.inputFiles[key]!.name,
            ...[translateInputFileManager.ARRIVAL_MAPPER]
          );
          await progressUpdate(state.inputFiles[key]!.name + t('読み込み中・・・'), 0);
          break;
        case '発注取込':
          await createOrderBusinessFromXlsx();
          await progressUpdate(state.inputFiles[key]!.name + t('読み込み中・・・'), 0);
        default:
          break;
      }
    }

    if (invalidExcelInputInfoType.length) {
      console.error(excelInputInfo, invalidExcelInputInfoType);
      await showInfoModal({
        modalTitle: t('取り込んだファイルに対応する読み込み設定がありません。'),
        modalDescription: h('div', { style: { display: 'grid', gap: '8px' } }, [
          t('以下のファイルに対応する読み込み設定がありません。設定を行ってから再度登録してください。'),
          h(
            'div',
            { style: { fontSize: '18px' } },
            invalidExcelInputInfoType.map((type) => t(type) + (invalidExcelInputInfoType.at(-1) == type ? '' : ', '))
          ),
        ]),
        backButtonLabel: t('OK'),
        icon: 'error',
        colorStyle: 'error',
        isDisabledNextButton: true,
      });
      return false;
    }

    if (deferredAnnounceFile) {
      Object.values(state.testXlsxFormMergedInputArray).forEach((business) => {
        business.customerAnnounce = undefined;
      });
      await createIBusinessFromZip(deferredAnnounceFile, true);
    }

    // 最新の在庫年月を取得する
    if (state.importInventoryExcelResult.isImport) {
      state.importInventoryExcelResult.latestInventoryYearMonth = InventoryService.getLatestInventory(
        Object.values(state.testXlsxFormMergedInputArray)
      );
      if (!state.importInventoryExcelResult.latestInventoryYearMonth) {
        // 一応、情報が取れなかった場合はエラーモーダルを出現させる
        await openOkInformationModal('error', t('エラー'), 'OK', t('在庫情報の最新年月取得に失敗しました。'));
        removeReadFileInfo();
      }
    }
    await progressUpdate(t('入力ファイルの統合中・・・'), 100);
    return true;
  };

  // vue-dropで受け取ったファイル名を取り出す
  /**
   * ファイル名を取得する
   * @param fileType - ファイルタイプ
   */
  const fetchFileName = (fileType: TranslateKeyName | OutputFileType): string | undefined => {
    return state?.xlsxFormFileInfo[fileType]?.name;
  };
  // ゴミ箱ボタンを押したファイルをstate.inputFilesから削除する
  /**
   * ファイルを削除する
   * @param file - ファイル
   * @param fileType - ファイルタイプ
   */
  const removeFile = (file: any, fileType: TranslateKeyName | OutputFileType): void => {
    if (state.inputFiles[fileType]?.name === file.name) {
      state.inputFiles[fileType] = undefined;
    }
  };

  // Event型から安全にnameを取り出す
  /**
   * イベントからファイル名を取得する
   * @param file - イベント
   */
  const fetchNameForEvent = (file: Event): string => {
    if (file instanceof File) {
      return file.name;
    }
    return '';
  };

  // 同じ属性のエクセルファイルが入力された場合にアラートを出力する
  /**
   *  同じ属性のエクセルファイルが入力された場合にアラートを出力する
   * @param fileNames - ファイル名
   */
  const showFileAttributeDuplicateErrorAlert = (fileNames: string[]) => {
    const errorTitle = `${t('同じ属性のファイルを2つ以上入力することはできません。')}\n\n`;
    const alertMessage = `${fileNames.join('\n')}`;
    const subMessage = `\n\n${t('追加したいファイルがある場合、同じ属性のファイルを削除してから再入力してください。')}`;
    alert(errorTitle + alertMessage + subMessage);
  };

  // 同じ名前のエクセルファイルが入力された場合にアラートを出力する
  /**
   * 同じ名前のエクセルファイルが入力された場合にアラートを出力する
   * @param fileNames - ファイル名
   */
  const showFileNameDuplicateErrorAlert = (fileNames: string[]) => {
    const errorTitle = `${t('同じ名前のファイルを2つ以上入力することはできません。')}\n\n`;
    const alertMessage = `${fileNames.join('\n')}`;
    alert(errorTitle + alertMessage);
  };

  /**
   * プログレスモーダルを表示して処理を実行する
   * @param title - モーダルタイトル
   * @param executor - 処理本体
   * @returns モーダルの操作結果
   */
  const runWithProgressModal = async (
    title: string,
    executor: (closeModal: () => void) => Promise<{ ok: boolean; value?: Record<string, unknown> }>
  ): Promise<ModalActionResult<any>> => {
    let closeHandler: (() => void) | undefined;
    let capturedError: unknown;
    let isClosed = false;
    let actionResult = createModalActionResult('dismiss');

    progressValue.value = 0;
    progressTaskMessage.value = t('準備中・・・');

    /**
     * プログレスモーダルを閉じる
     */
    const closeModal = () => {
      if (isClosed) return;
      isClosed = true;
      closeHandler?.();
    };

    /**
     * プログレスメッセージを取得する
     * @returns プログレスメッセージ
     */
    const getProgressMessage = () => progressTaskMessage.value;

    /**
     * InfoModal初期化時のハンドラ
     * @param helpers - InfoModalの制御ヘルパー
     */
    const handleProgressInitialized = (helpers: { onNext: () => void }) => {
      /**
       * InfoModalを閉じる処理を保持する
       */
      closeHandler = () => helpers.onNext();
    };

    /**
     * InfoModalマウント後に実行する処理
     * @param closeFn - モーダルを閉じる関数
     */
    const handleProgressMounted = async (closeFn: () => void) => {
      try {
        const { ok: isSuccess } = await executor(closeFn);
        actionResult = isSuccess ? createModalActionResult('confirm') : createModalActionResult('dismiss');
      } catch (error) {
        capturedError = error;
      } finally {
        closeFn();
      }
    };

    const modalPromise = showInfoModal({
      modalTitle: title,
      modalDescription: t('このままの画面でお待ちください。'),
      icon: 'loading',
      enableOutsideClick: false,
      backButtonLabel: '',
      nextButtonLabel: '',
      modalWidth: 640,
      isDisabledScroll: true,
      progress: {
        label: t('進捗'),
        message: getProgressMessage,
        value: progressValue,
        max: 100,
      },
      onInitialized: handleProgressInitialized,
      /**
       * モーダル表示後に処理を開始する
       */
      onMounted: async () => handleProgressMounted(closeModal),
    });

    await modalPromise;

    if (capturedError) {
      throw capturedError;
    }

    return actionResult;
  };
  // 処理中モーダル(任意書式で取り込みできるバージョン)
  /**
   * 処理中モーダル
   */
  const _openInputFilesLoadingModal = async (): Promise<ModalActionResult<any>> => {
    return runWithProgressModal(t('入力ファイルの読み取り中・・・'), async () => {
      const readResult = await _readInputExcel();
      if (!readResult) {
        removeAllFile();
        return { ok: false };
      }
      const validateResult = await validateInputFile();
      if (!validateResult) {
        removeAllFile();
        return { ok: false };
      }
      return { ok: true };
    });
  };

  /**
   * 入力ファイルのバリデーションを行う(エラー時はモーダルを発火させる)
   */
  const validateInputFile = async () => {
    const dailyAnnounceR15StartDayErrorObj = state.fileReadErrors
      .filter((item) => item.key === 'DailyAnnounceR15StartDay')
      .map((item) => ({ ...item }));

    const inappropriateTypeErrorObj = state.fileReadErrors
      .filter((item) => item.errCode === 'InappropriateType')
      .map((item) => ({ ...item }));

    const inputExcelInCorrectTypeErrorObj = state.fileReadErrors
      .filter((item) => item.errCode === 'InputExcelInCorrectType')
      .map((item) => ({ ...item }));

    if (dailyAnnounceR15StartDayErrorObj.length > 0) {
      const latestError = dailyAnnounceR15StartDayErrorObj[dailyAnnounceR15StartDayErrorObj.length - 1];
      const targetFileName = latestError.errorFileName?.inputFileName ?? '';
      const targetCell = latestError.errorCellNumber?.inputCellNumber ?? DAILY_ANNOUNCE_START_DAY_CELL;
      await openOkInformationModal(
        'error',
        t('フォーマットエラー'),
        t('確認'),
        undefined,
        t(`日別内示はセル${DAILY_ANNOUNCE_START_DAY_CELL}を必ず1日としてください。`) +
          (targetFileName ? `<br>${t('ファイル')}: ${targetFileName}` : '') +
          `<br>${t('セル')}: ${targetCell}`
      );
      return false;
    }

    if (inappropriateTypeErrorObj.length === 0 && inputExcelInCorrectTypeErrorObj.length === 0) return true;

    if (inappropriateTypeErrorObj.length > 0) {
      const errorFilesHtml = inappropriateTypeErrorObj
        .map((value) => `<div class="allWord">${value.errorFileName.inputFileName}</div>`)
        .join('');
      await openOkInformationModal(
        'error',
        t('入力ファイルフォーマットエラー'),
        t('OK'),
        undefined,
        `<h4>${t('以下の入力ファイルにフォーマットが異なるものがあります')}</h4>
                <br></br>
              <div class="error-files">${errorFilesHtml}</div>`
      );
      return false;
    }

    if (inputExcelInCorrectTypeErrorObj.length > 0) {
      await translateErrorModal(
        inputExcelInCorrectTypeErrorObj,
        t('入力エクセルのフォーマットエラー'),
        t('入力エクセルのフォーマットが異なるものがあります。')
      );
      return false;
    }

    return false;
  };

  /**
   * 処理中モーダル
   */
  const _openTranslateExcelLoadingModal = async () => {
    return await runWithProgressModal(t('転記中・・・'), async (closeModal) => {
      state.activeLoading = true;
      try {
        if (state.outputOrderFile || state.outputSupplierNotificationFile) {
          return { ok: true };
        }
        let updatedBusinessInfoCounts = {
          inventory: 0,
          arrival: 0,
          customerForecast: 0,
          salesResult: 0,
        };
        // 取引登録処理
        if (Object.entries(state.importFlags).some(([k, v]) => k !== '発注取込' && v)) {
          if (Object.keys(state.testXlsxFormMergedInputArray).length <= 0) {
            await openFailToSavedModal();
            return { ok: false };
          }
          const copyFormArray: Record<string, TranslateExcelBusiness> = simpleCopy(state.testXlsxFormMergedInputArray);
          const registerResult = await registerBusinessForTranslateExcelBusiness(
            state.testXlsxFormMergedInputArray,
            state.executionDate,
            state.successTranslateKeys,
            false,
            true,
            progressUpdate
          );

          if (registerResult.status.type === 'error') {
            removeAllFile();
            closeModal();
            if (registerResult.status.error.code === 'NOT_REGISTERED_BUSINESS') {
              await openOkInformationModal(
                'error',
                t('取引設定：アラートタイトル：一致取引なし'),
                'OK',
                t('取引設定：アラート文言：一致取引なし')
              );
            } else {
              await openOkInformationModal(
                'error',
                registerResult.status.error.statusCode + '：' + registerResult.status.error.code,
                'OK',
                registerResult.status.error.message
              );
            }
            return { ok: false };
          }

          const existBusinessInfoKeys: string[] = registerResult.status.data.existBusinessInfoKeys;
          state.successTranslateKeys = Object.fromEntries(existBusinessInfoKeys.map((key) => [key, true]));

          const unmanagedBusinessArray: IUnmanagedBusiness[] = [];
          Object.keys(copyFormArray).forEach((key) => {
            if (!state.successTranslateKeys[key]) {
              const failedUpdateBusinessObject: TranslateExcelBusiness = simpleCopy(copyFormArray[key]);
              for (const excelInfo of failedUpdateBusinessObject.excelInfos ?? []) {
                if (!excelInfo.rowObjectInfo.translateBusinessId && !excelInfo.rowObjectInfo.businessNumber) continue;
                const importFileType = UnmanagedBusinessService.covertToImportFileTypeFromExcelFileName(
                  typeof excelInfo.fileName === 'string' ? excelInfo.fileName : ''
                );
                if (importFileType === null) {
                  return;
                }

                unmanagedBusinessArray.push(
                  UnmanagedBusinessFactory.create({
                    businessNumber: failedUpdateBusinessObject.businessNumber,
                    businessName: failedUpdateBusinessObject.businessName,
                    supplierNumber: failedUpdateBusinessObject.supplierNumber,
                    supplierName: failedUpdateBusinessObject.supplierName,
                    salesDestinationNumber: failedUpdateBusinessObject.salesDestinationNumber,
                    salesDestinationName: failedUpdateBusinessObject.salesDestinationName,
                    warehouseNumber: failedUpdateBusinessObject.warehouseNumber,
                    warehouse: failedUpdateBusinessObject.warehouse,
                    package: failedUpdateBusinessObject.package,
                    unit: failedUpdateBusinessObject.unit,
                    leadTime: failedUpdateBusinessObject.leadTime,
                    leadTimeUnit: failedUpdateBusinessObject.leadTimeUnit,
                    moq: failedUpdateBusinessObject.MOQ,
                    importFileType: importFileType,
                    inputDate: DateService.convertDateToIDate(new Date()),
                    fileName: typeof excelInfo.fileName === 'string' ? excelInfo.fileName : '',
                    announce: failedUpdateBusinessObject.customerAnnounce,
                    customerNumber: failedUpdateBusinessObject.customerNumber,
                    customerName: failedUpdateBusinessObject.customerName,
                    salesResults: failedUpdateBusinessObject.salesResults,
                    stdInventoryIndices: failedUpdateBusinessObject.stdInventoryIndices,
                    inventory: failedUpdateBusinessObject.mvpInventory?.map((inventory) => inventory.amount)[0],
                    inventories: failedUpdateBusinessObject.mvpInventory,
                    arrival: failedUpdateBusinessObject.arrivals?.reduce((prev, current) => {
                      return prev + current.amount;
                    }, 0),
                    arrivals: failedUpdateBusinessObject.arrivals,
                    manufacturer: failedUpdateBusinessObject.manufacturer,
                    endUser: failedUpdateBusinessObject.endUser,
                  })
                );
              }
            }
          });

          const targetUpdateFileTypes: ImportFileType[] = Object.values(state.inputFiles)
            .map((file) => {
              if (file && file.name)
                return UnmanagedBusinessService.covertToImportFileTypeFromExcelFileName(typeof file.name === 'string' ? file.name : '');
            })
            .filter((fileName) => fileName != null) as ImportFileType[];

          diContainer.deleteByFileTypeUnmanagedBusinessUseCase.execute(targetUpdateFileTypes);
          diContainer.bulkSaveUnmanagedBusinessUseCase.execute(unmanagedBusinessArray);

          updatedBusinessInfoCounts = {
            ...registerResult.status.data.updatedBusinessInfoCounts,
          };
        }

        let updatedOrderCounts: RegisterOrderBusinessResultCounts = {
          registered: {
            updated: 0,
            linkedOrderNumberByIssued: 0,
            linkedOrderNumberByEtaAmount: 0,
            unmatched: 0,
          },
          alerted: {
            orderWithoutNumber: 0,
            outOfStockOrder: 0,
            overdueOrder: 0,
          },
        };

        // 発注登録処理
        if (state.importFlags.発注取込) {
          if (Object.keys(state.orderBusinessArray).length <= 0) {
            await openOkInformationModal(
              'error',
              t('発注の登録に失敗しました'),
              'OK',
              t('時間をおいて再度お試しください。なお、その他の情報については既に登録されています。')
            );
          } else {
            const copyFormOrderBusinessArray: Record<string, TranslateExcelOrderBusiness> = simpleCopy(state.orderBusinessArray);
            const orderBusinessRegisterResult = await diContainer.bulkRegisterOrderBusinessUseCase.execute(copyFormOrderBusinessArray);
            if (orderBusinessRegisterResult.status.type === 'success') {
              updatedOrderCounts = orderBusinessRegisterResult.status.data;
            } else {
              await openOkInformationModal(
                'error',
                t('発注の登録に失敗しました'),
                'OK',
                t('時間をおいて再度お試しください。なお、その他の情報については既に登録されています。')
              );
            }
          }
        }

        await progressUpdate(t('システムへ情報を登録中・・・'), 100);

        await new Promise((resolve: any) => {
          setTimeout(() => {
            resolve();
          }, 500);
        });

        closeModal();

        await openResultRegisteredAnnounceModal();

        await openOkInformationModal(
          'success',
          t('成功'),
          'OK',
          undefined,
          t('入力エクセル更新結果(モーダルテキスト)', {
            inventoryCount: updatedBusinessInfoCounts.inventory,
            arrivalCount: updatedBusinessInfoCounts.arrival,
            salesResultCount: updatedBusinessInfoCounts.salesResult,
            customerForecastCount: updatedBusinessInfoCounts.customerForecast,
            updatedOrderCount: updatedOrderCounts.registered.updated,
            linkedOrderNumberCount:
              updatedOrderCounts.registered.linkedOrderNumberByIssued + updatedOrderCounts.registered.linkedOrderNumberByEtaAmount,
            unmatchedOrderCount: updatedOrderCounts.registered.unmatched,
          })
        );
        removeAllFile();
        return { ok: true, value: { resCount: { ...updatedBusinessInfoCounts, ...updatedOrderCounts } } };
      } finally {
        state.activeLoading = false;
      }
    });
  };

  /**
   * プログレスバーの数値情報の更新を行う
   * @param progressTaskName - プログレスバーのタスク名
   * @param addValue - プログレスバーの値
   */
  const progressUpdate = async (progressTaskName: string, addValue: number) => {
    progressTaskMessage.value = progressTaskName;
    progressValue.value = Math.min(progressValue.value + addValue, 100);
  };

  /**
   * InfoModalで取引重複エラーモーダルを出現させる
   * 取引重複エラーがある場合に転記を続行するか、csvを出力して修正を行うかを問う
   * または、取引重複エラーを無視してダウンロードを行なった場合にforceDownloadFlg=trueでファイルダウンロード後に再度取引重複エラーのダウンロードを促す
   * @param outputFileContents - ファイル出力に使用する情報群
   * @param inputDuplicateFromKey - 転記するファイルタイプ(在庫表or仕入先内示)
   * @param forceDownloadFlg - 取引重複エラーを無視して実行したかのフラグ
   */
  const duplicateErrorModal = async (
    outputFileContents: OutputFileContents,
    inputDuplicateFromKey?: OutputFileType,
    forceDownloadFlg = false
  ) => {
    let confirmButtonText = t('取引重複.csvを保存して終了する');
    let denyButtonText = t('続行する');
    let alertHtmlText = `<div data-testid="duplicate_error_modal">${t('取引の重複があります')}<br/>${t(
      'このまま転記を行うと、数量データに不正確なものが入る場合があります'
    )}<br/>${t('このまま転記を続行しますか？')}</div>`;
    const translateOutputFileManager = new TranslateOutputFileManager(
      translateInputFileManager.getLatestYearMonthData(),
      state.inputExcelTypes
    );

    // 取引重複エラーを無視してファイルのダウンロードが行われた際のモーダル項目設定
    if (forceDownloadFlg) {
      confirmButtonText = t('保存');
      denyButtonText = t('キャンセル');
      alertHtmlText = `<div data-testid="force_download_duplicate_error_modal">${t(
        '取引重複.csvを確認し、取引の重複を解消してください。'
      )}</div>`;
    }
    // 重複取引が存在していた場合、エラーを出しダウンロードできるように
    const result = await openConfirmModal('error', t('エラーがあります。'), confirmButtonText, denyButtonText, '', alertHtmlText, {
      testId: 'duplicated_error_download_modal_container',
    });

    if (forceDownloadFlg) {
      if (!inputDuplicateFromKey) inputDuplicateFromKey = '在庫表';
      if (result.isConfirmed) {
        Object.keys(outputFileSelectionButton.value).forEach((key) => {
          outputFileSelectionButton.value[key].active = false;
          if (key === '取引重複エラー') outputFileSelectionButton.value[key].active = true;
        });
        translateOutputFileManager.downloadResultOfCreateOrderExcel(
          outputFileContents,
          outputFileSelectionButton.value,
          inputDuplicateFromKey
        );
      } else if (result.isDenied) {
        openOkInformationModal('info', t('ダウンロードをキャンセルしました'), t('閉じる'));
      }
    } else {
      if (result.isConfirmed) {
        Object.keys(outputFileSelectionButton.value).forEach((key) => {
          outputFileSelectionButton.value[key].active = false;
          if (key === '取引重複エラー') outputFileSelectionButton.value[key].active = true;
        });
        translateOutputFileManager.downloadResultOfCreateOrderExcel(outputFileContents, outputFileSelectionButton.value, '在庫表');
        removeAllFile();
      } else if (result.isDenied) {
        fileDownloadModal(outputFileContents);
      }
    }
  };

  /**
   * InfoModalでエラーモーダルを出現させ、csvを出力する
   * @param errorOutput - エラー情報
   * @param modalTitle - モーダルタイトル
   * @param modalContentStr - モーダルコンテンツ
   */
  const translateErrorModal = async (errorOutput: ExcelTranslateError[], modalTitle: string, modalContentStr: string) => {
    const createCsvUseCase = new CreateCsvUseCase();
    /**
     * エラーログデータを作成する
     * @param errObj - エラーオブジェクト
     */
    const createErrorLogData = (errObj: ExcelTranslateError[]): string[][] => {
      const translateErrorLog: string[][] = [];
      // ヘッダー
      translateErrorLog.push(['エラー名', '対象ファイル', 'シート名', '対象セル', 'エラー詳細']);

      errObj.forEach((err) => {
        translateErrorLog.push([
          err.errCode.escapeStringComma(),
          err.errorFileName.inputFileName.escapeStringComma(),
          String(err.errorSheetName?.inputSheetName ?? '').removeNewLineForText(),
          err.errorCellNumber.inputCellNumber.escapeStringComma(),
          String(err.errorString).escapeStringComma().removeNewLineForText(),
        ]);
      });
      return translateErrorLog;
    };

    const confirmButtonText = t('エラーまとめ.csvを保存して終了する');
    const denyButtonText = t('終了する');
    const alertHtmlText = modalContentStr;

    await openConfirmModal('error', modalTitle, confirmButtonText, denyButtonText, '', alertHtmlText).then((result) => {
      if (result.isConfirmed) {
        createCsvUseCase.Execute(createErrorLogData(errorOutput), t('エラーまとめ'));
      }
      removeAllFile();
    });
  };

  /**
   * 日別内示の登録終了日指定モーダル初期値を取得する
   */
  const getInitialDailyAnnounceRegisterEndDate = (): Date => {
    let latestDailyAnnounceDate: Date | undefined;
    for (const business of Object.values(state.testXlsxFormMergedInputArray)) {
      for (const announceDaily of business.customerAnnounceDaily ?? []) {
        const announceDate = new Date(announceDaily.year, announceDaily.month - 1, announceDaily.day);
        if (!latestDailyAnnounceDate || announceDate.getTime() > latestDailyAnnounceDate.getTime()) {
          latestDailyAnnounceDate = announceDate;
        }
      }
    }
    return latestDailyAnnounceDate ?? new Date();
  };

  /**
   * 日別内示の登録終了年月日を指定するモーダルを表示する
   * @param initialDate - 初期表示日
   */
  const openDailyAnnounceRegisterEndDateModal = async (initialDate: Date): Promise<ModalActionResult<Date>> => {
    let selectedDate = new Date(initialDate);
    selectedDate.setHours(0, 0, 0, 0);
    let destroyCalendarApp: (() => void) | undefined;
    const modalResult = await showInfoModal({
      modalTitle: t('取込む日別内示情報の登録終了年月日を指定してください'),
      modalDescription: undefined,
      noDescription: true,
      /**
       * カレンダー描画用のマウント先
       */
      modalContent: () => h('div', { id: 'daily-announce-register-end-date-modal' }),
      enableOutsideClick: false,
      backButtonLabel: t('キャンセル'),
      nextButtonLabel: t('次へ'),
      testId: 'daily_announce_register_end_date_modal_container',
      /**
       * カレンダーコンポーネントをモーダルにマウントする
       */
      onMounted: () => {
        const wrapper = document.createElement('div');
        /**
         * カレンダーで選択した日付を登録終了日に反映する
         * @param date - 選択した日付
         */
        const updateRegisterEndDate = (date: Date | null) => {
          if (!date) return;
          const normalizedDate = new Date(date);
          normalizedDate.setHours(0, 0, 0, 0);
          selectedDate = normalizedDate;
        };
        const app = createApp({
          /**
           * カレンダー描画関数
           */
          setup() {
            return () =>
              h(Calendar, {
                modelValue: selectedDate,
                hideTime: true,
                'onUpdate:modelValue': updateRegisterEndDate,
              });
          },
        });
        app.mount(wrapper);
        destroyCalendarApp = app.unmount.bind(app);
        document.getElementById('daily-announce-register-end-date-modal')?.appendChild(wrapper);
      },
    });
    destroyCalendarApp?.();
    return modalResult ? createModalActionResult('confirm', selectedDate) : createModalActionResult('deny', selectedDate);
  };

  /**
   * 日別内示の登録終了年月日を適用し、対象外データを除外する
   * @param registerEndDate - 取り込み対象の終了日
   */
  const applyDailyAnnounceRegisterEndDate = (registerEndDate: Date) => {
    const registerEndDateAtEndOfDay = new Date(registerEndDate);
    registerEndDateAtEndOfDay.setHours(23, 59, 59, 999);
    for (const business of Object.values(state.testXlsxFormMergedInputArray)) {
      if (!business.customerAnnounceDaily) continue;
      business.customerAnnounceDaily = business.customerAnnounceDaily.filter((announceDaily) => {
        const announceDate = new Date(announceDaily.year, announceDaily.month - 1, announceDaily.day);
        return announceDate.getTime() <= registerEndDateAtEndOfDay.getTime();
      });
    }
  };

  /**
   * 日別内示の対象日数が93日以上か判定する
   */
  const hasDailyAnnouncePeriodOver93Days = (): boolean => {
    let earliestDate: Date | undefined;
    let latestDate: Date | undefined;
    for (const business of Object.values(state.testXlsxFormMergedInputArray)) {
      for (const announceDaily of business.customerAnnounceDaily ?? []) {
        const announceDate = new Date(announceDaily.year, announceDaily.month - 1, announceDaily.day);
        if (!earliestDate || announceDate.getTime() < earliestDate.getTime()) {
          earliestDate = announceDate;
        }
        if (!latestDate || announceDate.getTime() > latestDate.getTime()) {
          latestDate = announceDate;
        }
      }
    }
    if (!earliestDate || !latestDate) return false;
    const periodDays = Math.floor((latestDate.getTime() - earliestDate.getTime()) / DAY_STD) + 1;
    return periodDays >= DAILY_ANNOUNCE_PERIOD_THRESHOLD_DAYS;
  };

  /**
   * 仕入先×年月単位で内示数量を集計する
   * @param announceType - 月次内示か日別内示か
   */
  const aggregateAnnounceAmountBySupplierYearMonth = (announceType: 'monthly' | 'daily'): Map<string, number> => {
    const amountsBySupplierYearMonth = new Map<string, number>();

    /**
     * 内示比較用の一意キー（6キー）を作成する
     * @param business - 取引情報
     */
    const buildBusinessComparisonKey = (business: TranslateExcelBusiness): string => {
      if (business.translateBusinessId) return String(business.translateBusinessId);
      return [
        business.warehouseNumber ?? '',
        business.businessNumber ?? '',
        business.package ?? '',
        business.supplierNumber ?? '',
        business.salesDestinationNumber ?? '',
        business.customerNumber ?? '',
      ].join('|');
    };

    for (const business of Object.values(state.testXlsxFormMergedInputArray)) {
      const businessComparisonKey = buildBusinessComparisonKey(business);
      if (!businessComparisonKey) continue;
      if (announceType === 'monthly') {
        for (const announce of business.customerAnnounce ?? []) {
          const aggregateKey = `${businessComparisonKey}:${announce.year}-${announce.month}`;
          const currentAmount = amountsBySupplierYearMonth.get(aggregateKey) ?? 0;
          const rawAmount = Number(announce.amount);
          const safeAmount = Number.isFinite(rawAmount) ? rawAmount : 0;
          amountsBySupplierYearMonth.set(aggregateKey, currentAmount + safeAmount);
        }
        continue;
      }
      for (const announceDaily of business.customerAnnounceDaily ?? []) {
        const aggregateKey = `${businessComparisonKey}:${announceDaily.year}-${announceDaily.month}`;
        const currentAmount = amountsBySupplierYearMonth.get(aggregateKey) ?? 0;
        const rawAmount = Number(announceDaily.amount);
        const safeAmount = Number.isFinite(rawAmount) ? rawAmount : 0;
        amountsBySupplierYearMonth.set(aggregateKey, currentAmount + safeAmount);
      }
    }
    return amountsBySupplierYearMonth;
  };

  /**
   * 月次内示データが1件以上存在するか判定する
   */
  const hasMonthlyAnnounceData = (): boolean => {
    return Object.values(state.testXlsxFormMergedInputArray).some((business) => (business.customerAnnounce?.length ?? 0) > 0);
  };

  /**
   * 日別内示データが1件以上存在するか判定する
   */
  const hasDailyAnnounceData = (): boolean => {
    return Object.values(state.testXlsxFormMergedInputArray).some((business) => (business.customerAnnounceDaily?.length ?? 0) > 0);
  };

  /**
   * 月次内示と日別内示の合計値が一致しないものがあるか確認する
   */
  const hasMismatchMonthlyAndDailyAnnounce = (): boolean => {
    const monthlyAnnounceAmounts = aggregateAnnounceAmountBySupplierYearMonth('monthly');
    const dailyAnnounceAmounts = aggregateAnnounceAmountBySupplierYearMonth('daily');
    const compareKeys = new Set([...monthlyAnnounceAmounts.keys(), ...dailyAnnounceAmounts.keys()]);
    for (const key of compareKeys) {
      const monthlyAmount = monthlyAnnounceAmounts.get(key) ?? 0;
      const dailyAmount = dailyAnnounceAmounts.get(key) ?? 0;
      if (Math.abs(monthlyAmount - dailyAmount) > ANNOUNCE_AMOUNT_COMPARISON_TOLERANCE) {
        return true;
      }
    }
    return false;
  };

  /**
   * 月次内示と日別内示の不整合確認モーダルを表示する
   */
  const openDailyAnnounceMismatchConfirmModal = async (): Promise<ModalActionResult<any>> => {
    return openConfirmModal(
      'error',
      t('月次の内示と日別内示の合計値が誤っています'),
      t('はい'),
      t('いいえ'),
      t('整合性が合っていませんが登録を進めますか？')
    );
  };

  /**
   * InfoModalとdatepickerを用いて取り込む在庫の年月日を入力させる
   * TODO: if分のネストどうにかしたいけど、何かいい案あったら修正する
   */
  const openTranslateExcelModal = async () => {
    // 在庫エクセルが入力されているかの情報
    state.importInventoryExcelResult.isImport = state.inputFiles['在庫'] !== undefined;
    // 内示エクセルが入力されているかの情報
    state.importAnnounceExcelResult.isImport = state.importFlags['内示'];
    // 日別内示エクセルが入力されているかの情報
    state.importAnnounceDailyExcelResult.isImport = state.importFlags['日別内示'];

    // 入力エクセルの読み取りを行う
    const inputLoadingResult = await _openInputFilesLoadingModal();
    if (!inputLoadingResult.isConfirmed) {
      removeAllFile();
      return;
    }

    // 内示がインポートされている場合、受領年月が怪しい時に確認モーダルを出す
    if (state.importAnnounceExcelResult.isImport) {
      const forecastReceivedDateDifferenceList = getForecastReceivedDateDifferenceList(false);
      if (forecastReceivedDateDifferenceList.length > 0) {
        await openOkInformationModal(
          'info',
          t('内示の年月が揃っていません'),
          t('OK'),
          undefined,
          `${t('内示の年月情報がZipファイル内のエクセルで揃っていません。')}<br>
            ${t('当該ファイル名:')}<br>
            ${forecastReceivedDateDifferenceList.map(([key]) => `${key.split('/')[1]}`).join('<br>')}`
        );
        removeAllFile();
        return;
      }
      const [isForecastReceivedDateInvalid, criteriaReceiveDate] = getIsForecastReceivedDateInvalidAndCriteria(false);
      if (isForecastReceivedDateInvalid) {
        const confirmInvalidReceivedDate = await openConfirmModal(
          'info',
          t('内示の年月が誤っている可能性があります'),
          t('続行'),
          t('キャンセル'),
          undefined,
          `${t('内示の年月が先々月以前から、もしくは再来月以降からになっています。')}<br>
          ${t('想定外のデータが転記される可能性があります。')}<br>
          ${t('設定された受領年月: ')}${t('displayShortDate', {
            year: criteriaReceiveDate.year,
            month: criteriaReceiveDate.month,
          })}<br>
          ${t('続行しますか？')}`
        );
        if (!confirmInvalidReceivedDate.isConfirmed) {
          // 続行ボタン押下以外の場合は読み取り情報を初期化してreturn
          removeAllFile();
          return;
        }
      }
    }

    if (state.importAnnounceDailyExcelResult.isImport) {
      const forecastReceivedDateDifferenceList = getForecastReceivedDateDifferenceList(true);
      if (forecastReceivedDateDifferenceList.length > 0) {
        await openOkInformationModal(
          'info',
          t('日別内示の年月が揃っていません'),
          t('OK'),
          undefined,
          `${t('日別内示の年月情報がZipファイル内のエクセルで揃っていません。')}<br>
            ${t('当該ファイル名:')}<br>
            ${forecastReceivedDateDifferenceList.map(([key]) => `${key.split('/')[1]}`).join('<br>')}`
        );
        removeAllFile();
        return;
      }
      const [isForecastReceivedDateInvalid, criteriaReceiveDate] = getIsForecastReceivedDateInvalidAndCriteria(true);
      if (isForecastReceivedDateInvalid) {
        const confirmInvalidReceivedDate = await openConfirmModal(
          'info',
          t('日別内示の年月が誤っている可能性があります'),
          t('続行'),
          t('キャンセル'),
          undefined,
          `${t('日別内示の年月が先々月以前から、もしくは来月以降からになっています。')}<br>
          ${t('想定外のデータが転記される可能性があります。')}<br>
          ${t('設定された受領年月: ')}${t('displayShortDate', {
            year: criteriaReceiveDate.year,
            month: criteriaReceiveDate.month,
          })}<br>
          ${t('続行しますか？')}`
        );
        if (!confirmInvalidReceivedDate.isConfirmed) {
          removeAllFile();
          return;
        }
      }

      if (hasDailyAnnouncePeriodOver93Days()) {
        const dailyAnnounceRegisterEndDateResult = await openDailyAnnounceRegisterEndDateModal(getInitialDailyAnnounceRegisterEndDate());
        if (!dailyAnnounceRegisterEndDateResult.isConfirmed) {
          removeAllFile();
          return;
        }
        const registerEndDate = dailyAnnounceRegisterEndDateResult.value ?? getInitialDailyAnnounceRegisterEndDate();
        applyDailyAnnounceRegisterEndDate(registerEndDate);
      }
      const canCompareMonthlyAndDailyAnnounce = hasMonthlyAnnounceData() && hasDailyAnnounceData();
      if (canCompareMonthlyAndDailyAnnounce && hasMismatchMonthlyAndDailyAnnounce()) {
        const mismatchConfirmModalResult = await openDailyAnnounceMismatchConfirmModal();
        if (!mismatchConfirmModalResult.isConfirmed) {
          removeAllFile();
          return;
        }
      }
    }

    // 在庫年月日選択モーダルの表示判定
    if (state.importInventoryExcelResult.isImport) {
      // 在庫年月日選択モーダルを表示する
      const isResult = await openExecutionDateModal();
      // 在庫年月日選択モーダルの状態判定
      if (!isResult.isConfirmed) {
        // モーダルを閉じて処理を中断した場合、読み取り情報を初期化
        removeAllFile();
        return;
      }
    }

    // 転記処理を実行する
    const result = await _openTranslateExcelLoadingModal();

    let isPushToOrderPage = false;
    if (result.value?.resCount && hasAlertCount(result.value.resCount)) {
      isPushToOrderPage = await showInfoModal({
        modalTitle: t('一部の発注にアラートが出ています'),
        modalContent: showOrderImportSuccessModalContent([
          { data: result.value.resCount.alerted.orderWithoutNumber ?? 0, title: '発注番号未登録' },
          {
            data: result.value.resCount.alerted.overdueOrder ?? 0,
            title: '納期が今日以前の発注',
          },
          {
            data: result.value.resCount.alerted.outOfStockOrder ?? 0,
            title: '在庫切れの可能性がある発注',
            isError: (result.value.resCount.alerted.outOfStockOrder ?? 0) > 0,
          },
        ]),
        nextButtonLabel: t('発注ページで確認する'),
        backButtonLabel: t('スキップ'),
      });
    }
    return { pushToOrderPage: isPushToOrderPage };
  };

  /**
   * InfoModalとdatepickerを用いて取り込む在庫の年月日を入力させる
   */
  const openExecutionDateModal = async (): Promise<ModalActionResult<any>> => {
    const nowDate = new Date();
    nowDate.setFullYear(state.importInventoryExcelResult.latestInventoryYearMonth.year);
    nowDate.setMonth(state.importInventoryExcelResult.latestInventoryYearMonth.month, 0);
    // 在庫表から取り込んだ最新年月情報が当月以降だった場合の処理
    if (new Date() < nowDate) {
      state.executionDate = new Date();
    } else {
      state.executionDate = nowDate;
    }
    //TODO: システムに登録した情報を表示する画面がまだ本番環境に無いので、ここら辺の機能を本番環境で封じる
    // ↑ 本番環境に取り込む段階で、分岐処理を外してやる
    const modalResult = await showInfoModal({
      modalTitle: t('今回取り込みを行う在庫の年月日を指定してください。'),
      modalDescription: undefined,
      /**
       * dateField描画用のマウント先
       */
      modalContent: () => h('div', { id: 'execution-date-modal', class: 'execution-date-modal' }),
      icon: 'info',
      enableOutsideClick: false,
      backButtonLabel: t('戻る'),
      nextButtonLabel: t('決定'),
      testId: 'execution_date_modal_container',
      /**
       * dateFieldをモーダルへマウントする
       */
      onMounted: () => {
        const wrapper = document.createElement('div');
        dateField(
          state.executionDate,
          true,
          (date: Date) => {
            state.executionDate = date;
          },
          DatePickerService.getFuncValidateCalendarDatesToCurrentMonthOnly(state.executionDate)
        ).mount(wrapper);
        document.getElementById('execution-date-modal')?.appendChild(wrapper);
      },
    });
    return modalResult ? createModalActionResult('confirm') : createModalActionResult('deny');
  };

  /**
   * InfoModalを用いてファイルダウンロードモーダルを出現させる
   * 取引重複エラーを無視してダウンロードを行なった場合は、ダウンロード後に取引重複エラー.csvのダウンロードを促す
   * @param outputFileContents - ファイル出力に使用する情報群
   */
  const fileDownloadModal = async (outputFileContents: OutputFileContents) => {
    const result = await openConfirmModal(
      'success',
      t('ファイルのダウンロードを行いますか?'),
      t('保存'),
      t(`キャンセル`),
      undefined,
      `<div data-testid="file_download_modal"><br/></div>`,
      { testId: 'file_download_modal_container' }
    );

    if (result.isConfirmed) {
      let inputDuplicateErrors: string[][] = [];
      let inputDuplicateFromKey: OutputFileType = '在庫表';
      const translateOutputFileManager = new TranslateOutputFileManager(
        translateInputFileManager.getLatestYearMonthData(),
        state.inputExcelTypes
      );

      if (outputFileContents['在庫表']) {
        translateOutputFileManager.downloadResultOfCreateOrderExcel(outputFileContents, outputFileSelectionButton.value, '在庫表');
        inputDuplicateErrors = outputFileContents['在庫表'][4];
      }
      if (outputFileContents['仕入先内示']) {
        translateOutputFileManager.downloadResultOfCreateOrderExcel(outputFileContents, outputFileSelectionButton.value, '仕入先内示');
        inputDuplicateErrors = outputFileContents['仕入先内示'][4];
        inputDuplicateFromKey = '仕入先内示';
      }
      if (inputDuplicateErrors.length > 0) {
        duplicateErrorModal(outputFileContents, inputDuplicateFromKey, true);
      }
    } else if (result.isDenied) {
      openOkInformationModal('info', t('ダウンロードをキャンセルしました'), t('閉じる'));
    }
    removeAllFile();
  };

  /**
   * InfoModalを用いてファイルダウンロードモーダルを出現させる
   * @param importFlags - インポートフラグ
   */
  const importData = (importFlags: { [key in TranslateKeyName | OutputFileType]: boolean }): boolean => {
    // 無効なファイルが選択されたどうかチェックする
    const correctFileNumber = Object.keys(importFlags).reduce((previous, currentKey) => {
      if (importFlags[currentKey]) return previous + 1;
      return previous;
    }, 0);
    return outputFlagSwitching(correctFileNumber);
  };

  /**
   * InfoModalを用いてファイルダウンロードモーダルを出現させる
   * @param importFlags - インポートフラグ
   */
  const importUpdateData = (importFlags: { [key in TranslateKeyName | OutputFileType]: boolean }): boolean => {
    // 無効なファイルが選択されたどうかチェックする
    const correctFileNumber = Object.keys(importFlags).reduce((previous, currentKey) => {
      if (importFlags[currentKey]) return previous + 1;
      return previous;
    }, 0);
    return correctFileNumber > 0;
  };

  /**
   * 取引登録結果モーダルを表示する
   */
  const openResultRegisteredAnnounceModal = async () => {
    // 内示ファイルがアップロードされていない場合はスキップ
    if (!state.importFlags['内示'] && !state.importFlags['日別内示']) return;
    // IBusinessにアップキャスト
    const translateExcelBusinessArray: TranslateExcelBusiness[] = Object.values(state.testXlsxFormMergedInputArray) ?? [];
    const businesses: IBusiness[] = extendedBusinessToIBusiness(translateExcelBusinessArray, translateExcelBusinessProperties);
    // 比較に使用するYearMonthを揃える
    const displayYearMonth = Array.from({ length: DISPLAY_FORECAST_PERIOD }, (_, num) =>
      DateService.getShiftedIYearMonthFromNowDateByMonths(num)
    );
    // ResultRegisteredAnnounceを作成する
    for (const business of businesses) {
      const supplierName = business.supplierName;
      const sumAmountMonthly = displayYearMonth.map((dYM) => calcSumAmountAnnounce(business, dYM));
      const sumAmountDaily = displayYearMonth.map((dYM) => calcSumAmountAnnounceDaily(business, dYM));

      const resultRegisteredAnnounce: ResultRegisteredAnnounce | undefined = state.resultRegisteredAnnounces.find(
        (item) => item.supplierName === supplierName
      );
      if (resultRegisteredAnnounce) {
        resultRegisteredAnnounce.sumAmountMonthly = resultRegisteredAnnounce.sumAmountMonthly.map((sAM, i) => (sAM += sumAmountMonthly[i]));
        resultRegisteredAnnounce.sumAmountDaily = resultRegisteredAnnounce.sumAmountDaily.map((sAD, i) => (sAD += sumAmountDaily[i]));
      } else {
        state.resultRegisteredAnnounces.push({
          supplierName,
          sumAmountMonthly,
          sumAmountDaily,
        });
      }
    }

    state.isOpenResultRegisteredAnnounceModal = true;

    // state.isOpenResultRegisteredAnnounceModalがfalseになるまで待つ
    await new Promise((resolve) => {
      const unwatch = watch(
        () => state.isOpenResultRegisteredAnnounceModal,
        (newValue) => {
          if (!newValue) {
            unwatch();
            resolve(null);
          }
        }
      );
    });

    // 初期化しておく
    state.resultRegisteredAnnounces = [];
  };

  /**
   * 取引登録結果モーダルを閉じる
   * @param business - 取引情報
   * @param yearMonth - 年月情報
   */
  const calcSumAmountAnnounce = (business: IBusiness, yearMonth: IYearMonth): number => {
    return (
      business.customerAnnounce
        ?.filter((item) => {
          return item.year === yearMonth.year && item.month === yearMonth.month;
        })
        .reduce((previous, current) => {
          return previous + current.amount;
        }, 0) ?? 0
    );
  };

  /**
   * 日別内示の月次合計を算出する
   * @param business - 取引情報
   * @param yearMonth - 年月情報
   */
  const calcSumAmountAnnounceDaily = (business: IBusiness, yearMonth: IYearMonth): number => {
    return (
      business.customerAnnounceDaily
        ?.filter((item) => {
          return item.year === yearMonth.year && item.month === yearMonth.month;
        })
        .reduce((previous, current) => {
          return previous + current.amount;
        }, 0) ?? 0
    );
  };

  /**
   * 内示の受領年月が揃っていないもののリストを返す
   * @param isDaily - 日次内示かどうか
   */
  const getForecastReceivedDateDifferenceList = (isDaily: boolean): [string, IYearMonth][] => {
    const entriesManageInitiatedForecastDate = getEntriesManageInitiatedForecastDate(isDaily);
    if (entriesManageInitiatedForecastDate.length < 1) return [];
    // 基準となる受領年月
    const criteriaReceiveDate = entriesManageInitiatedForecastDate[0][1];
    // 基準の受領年月と異なる受領年月になっているリストを取る
    return entriesManageInitiatedForecastDate.filter(
      ([_, yearMonth]) => DateService.compareYearMonths(yearMonth, criteriaReceiveDate) !== 0
    );
  };

  /**
   * 内示の受領年月が揃っていないもののリストを返す
   * @param isDaily - 日次内示かどうか
   */
  const getIsForecastReceivedDateInvalidAndCriteria = (isDaily: boolean): [false, null] | [boolean, IYearMonth] => {
    const entriesManageInitiatedForecastDate = getEntriesManageInitiatedForecastDate(isDaily);
    if (entriesManageInitiatedForecastDate.length < 1) return [false, null];
    // 受領年月は当月、先月と先々月を確認なしで許容
    const criteriaReceiveDate = entriesManageInitiatedForecastDate[0][1];
    const nowYearMonth = DateService.convertDateToIYearMonth(new Date());
    const oneBeforeYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(-1);
    const twoBeforeYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(-2);
    return [
      DateService.compareYearMonths(criteriaReceiveDate, nowYearMonth) !== 0 &&
        DateService.compareYearMonths(criteriaReceiveDate, oneBeforeYearMonth) !== 0 &&
        DateService.compareYearMonths(criteriaReceiveDate, twoBeforeYearMonth) !== 0,
      criteriaReceiveDate,
    ];
  };

  /**
   * 内示の各ファイルごとの受領月を取得する
   * @param isDaily - 日次内示かどうか
   */
  const getEntriesManageInitiatedForecastDate = (isDaily: boolean): [string, IYearMonth][] => {
    const target = isDaily
      ? state.importAnnounceDailyExcelResult.manageInitiatedForecastDate
      : state.importAnnounceExcelResult.manageInitiatedForecastDate;

    return Object.entries(target).filter(([key, _]) => key.endsWith('.xlsx'));
  };

  return {
    state,
    setupFileInputRef,
    outputFileSelectionButton,
    onFileRemoved,
    onDrop,
    dropzoneOptions2,
    getRootProps,
    getInputProps,
    isDragActive,
    removeAllFile,
    isZipFile,
    getFileSizeStr,
    handleClickDeleteFile,
    fileRead,
    createIBusinessFromZip,
    createIBusinessFromDailyZip,
    fetchFileName,
    fetchNameForEvent,
    openTranslateExcelModal,
    importData,
    importUpdateData,
  };
};

/**
 * このファイル内で定義されている関数を呼び出すためのインターフェース
 */
interface ITranslateExcelStore {
  state: FetchExcelModal;
  setupFileInputRef: (
    ref1: Ref<any>,
    ref2: Ref<any>,
    ref3: Ref<any>,
    ref4: Ref<any>,
    ref5: Ref<any>,
    ref6: Ref<any>,
    ref7: Ref<any>,
    ref8: Ref<any>
  ) => void;
  outputFileSelectionButton: Ref<IOutputFileInfo>;
  onFileRemoved: (file: any) => void;
  onDrop: (acceptFiles: any, rejectReasons: any) => void;
  dropzoneOptions2: Partial<FileUploadOptions>;
  getRootProps: any;
  getInputProps: any;
  isDragActive: Ref<boolean>;
  removeAllFile: () => void;
  isZipFile: (file: any) => boolean;
  getFileSizeStr: (file: any) => string;
  handleClickDeleteFile: (index: any) => void;
  fileRead: (e: any, fileType: TranslateKeyName | OutputFileType, isImportDragAndDrop: boolean, expectedFileExtension: any) => void;
  createIBusinessFromZip: (e: any, isImportDragAndDrop?: boolean) => void;
  createIBusinessFromDailyZip: (e: any, isImportDragAndDrop?: boolean) => void;
  fetchFileName: (fileType: TranslateKeyName | OutputFileType) => string | undefined;
  fetchNameForEvent: (file: Event) => string;
  openTranslateExcelModal: () => Promise<Record<string, unknown> | undefined>;
  importData: (importFlags: { [key in TranslateKeyName | OutputFileType]: boolean }) => boolean;
  importUpdateData: (importFlags: { [key in TranslateKeyName | OutputFileType]: boolean }) => boolean;
}

export const key: InjectionKey<ITranslateExcelStore> = Symbol('key');
